import { TYPESENSE_SELECT_OPTIONS_WINDOW_SIZE } from '../constants';
import {
  GetSelectOptionByIdFunction,
  SearchOptions,
  SelectOptionSearchFunction,
  TypesenseCollection,
} from '../types';
import { searchCollection, TypesenseDocument } from '../utils';
import { type ExpenseTypeOption } from './expenseTypes.types';

const BASE_FILTER_ACTIVE = 'IsActive:true';
const BASE_FILTER_GLOBAL_EXPENSE_TYPES = 'LegalEntityId:=-1';

export const getSearchExpenseTypeSelectOptions: SelectOptionSearchFunction<
  ExpenseTypeOption
> = async (
  searchValue: string,
  apiKey: string,
  pageIndex: number = 1,
  options: SearchOptions = {},
) => {
  const results = await searchCollection(
    TypesenseCollection.EXPENSE_TYPES.name,
    'Name',
    searchValue,
    apiKey,
    pageIndex,
    TYPESENSE_SELECT_OPTIONS_WINDOW_SIZE,
    options.filterBy
      ? `(${options.filterBy} && ${BASE_FILTER_ACTIVE}) || (${BASE_FILTER_GLOBAL_EXPENSE_TYPES} && ${BASE_FILTER_ACTIVE})`
      : `${BASE_FILTER_ACTIVE} || (${BASE_FILTER_GLOBAL_EXPENSE_TYPES} && ${BASE_FILTER_ACTIVE})`,
    options.sortBy ? `${options.sortBy}` : 'Name:asc',
  );
  if (!results.success || results.error) {
    throw new Error(
      results.error || 'Failed to fetch expenseTypes from Typesense',
    );
  }

  const mappedResults = results?.documents?.map((item: TypesenseDocument) => ({
    value: item.Name,
    id: item.SourceId.toString(),
    vat: item.Vat,
  }));
  return mappedResults ?? [];
};

/**
 * Retrieves a expenseType select option by ID from Typesense.
 * @throws {Error} When the search fails, returns an error, or no expenseType is found with the given ID
 */
export const getExpenseTypeSelectOptionById: GetSelectOptionByIdFunction<
  ExpenseTypeOption
> = async (
  id: string,
  apiKey: string,
  additionalFilterOptions?: SearchOptions['filterBy'],
) => {
  const results = await searchCollection(
    TypesenseCollection.EXPENSE_TYPES.name,
    'Name',
    '*',
    apiKey,
    1,
    1,
    additionalFilterOptions
      ? `(${additionalFilterOptions} || ${BASE_FILTER_GLOBAL_EXPENSE_TYPES}) && SourceId:=${id}`
      : `SourceId:=${id}`,
  );

  if (!results.success || results.error) {
    throw new Error(
      results.error || 'Failed to fetch expenseType by id from Typesense',
    );
  }

  const mappedResults = results?.documents?.map((item: TypesenseDocument) => ({
    value: item.Name,
    id: item.SourceId.toString(),
    vat: item.Vat,
  }));

  if (!mappedResults || mappedResults.length === 0) {
    //  debug only here as we just throw an error to be able to use try-catch, but it is not necessarily an actual error
    console.debug(`ExpenseType with id ${id} not found`);
    throw new Error(`ExpenseType with id ${id} not found`);
  }

  return mappedResults[0];
};
